import { Client, Platform, Scope } from '@foundation-player/runtime';
import { ClientScope } from '@foundation-player/runtime/dist/config/client';
import { ClientAndScope } from '@foundation-player/types';
import { Preset } from './preset';

export type RTLPlusClients =
  | typeof Client.RTLPlusWatch
  | typeof Client.RTLPlusRadio
  | typeof Client.RTLPlusAudiobook
  | typeof Client.RTLPlusPodcast;

/**
 * Maps a HTML5 Foundation Player's platform to a scope of a RTLPlus client.
 *
 * @remarks
 * This is specifically for RTL clients, because they use platform-like scopes to differentiate between different apps.
 * This is not about platforms of the Streaming Tech backend services.
 */
export function mapPlatformToRTLPlusScope<C extends RTLPlusClients>(
  client: C,
  platform: Platform,
): ClientScope<RTLPlusClients> | undefined {
  switch (platform) {
    case Platform.Web:
      return Scope[client].Web;
    case Platform.Tizen3:
    case Platform.Tizen4:
    case Platform.Tizen5:
    case Platform.Tizen5_5:
    case Platform.Tizen6:
    case Platform.Tizen6_5:
    case Platform.Tizen7:
    case Platform.Tizen8:
      return Scope[client].Tizen;
    case Platform.WebOS3:
    case Platform.WebOS4:
    case Platform.WebOS5:
    case Platform.WebOS6:
    case Platform.WebOS22:
    case Platform.WebOS23:
    case Platform.WebOS24:
    case Platform.WebOS25:
      return Scope[client].LG;
    case Platform.MagentaG3:
    case Platform.MagentaG4:
    case Platform.MagentaG5:
      return Scope[client].Magenta;
    case Platform.SkyQ:
      return Scope.rtlplusmpawatch.SkyQ;
    case Platform.SkyXiOne:
    case Platform.SkyStream:
      return Scope.rtlplusmpawatch.SkyStream;
    case Platform.VodafoneGTV:
      return Scope.rtlplusmpawatch.VodafoneGTV;
    case Platform.VodafoneVTV:
      return Scope.rtlplusmpawatch.VodafoneVTV;
    case Platform.TeleColumbus:
      return Scope.rtlplusmpawatch.TeleColumbus;
    case Platform.Kepler:
      return Scope[client].Kepler;
    default:
      return undefined;
  }
}

/**
 * Maps a HTML5 Foundation Player's platform to a scope of a SuperRTLToggo client.
 */
export function mapPlatformToSuperRTLToggoScope(
  platform: Platform,
): ClientScope<typeof Client.SuperRTLToggo> | undefined {
  switch (platform) {
    case Platform.Web:
      return Scope[Client.SuperRTLToggo].Web;
    case Platform.Tizen3:
    case Platform.Tizen4:
    case Platform.Tizen5:
    case Platform.Tizen5_5:
    case Platform.Tizen6:
    case Platform.Tizen6_5:
    case Platform.Tizen7:
    case Platform.Tizen8:
      return Scope[Client.SuperRTLToggo].Tizen;
    default:
      return undefined;
  }
}

/**
 * Converts a preset and platform to client options.
 *
 * @remarks
 * This function exists to solve backwards compatibility issues with the old preset system.
 * Not every preset has a direct mapping to a client and scope.
 */
export function mapToClientAndScope(preset: Preset | undefined, platform: Platform): ClientAndScope | undefined {
  switch (preset) {
    case Preset.NTV:
      return { client: Client.RTLDigitalNews, scope: Scope[Client.RTLDigitalNews].NTV };
    case Preset.MediaHub:
      return { client: Client.MediaHub };
    case Preset.SuperRTLToggo:
      return { client: Client.SuperRTLToggo, scope: mapPlatformToSuperRTLToggoScope(platform) };
    case Preset.GAndJ:
      return { client: Client.GrunerUndJahr };
    case Preset.RTLPlusAudiobook:
    case Preset.RTLPlusMusicAudiobook:
      return { client: Client.RTLPlusAudiobook, scope: mapPlatformToRTLPlusScope(Client.RTLPlusAudiobook, platform) };
    case Preset.RTLPlusPodcast:
    case Preset.RTLPlusMusicPodcast:
      return { client: Client.RTLPlusPodcast, scope: mapPlatformToRTLPlusScope(Client.RTLPlusPodcast, platform) };
    case Preset.RTLPlusRadio:
    case Preset.RTLPlusMusicRadio:
      return { client: Client.RTLPlusRadio, scope: mapPlatformToRTLPlusScope(Client.RTLPlusRadio, platform) };
    case Preset.RTLPlusWatch:
      return { client: Client.RTLPlusWatch, scope: mapPlatformToRTLPlusScope(Client.RTLPlusWatch, platform) };
    default:
      return undefined;
  }
}
